import { Autocomplete, Box, TextField } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { useFormikContext } from 'formik';
import { find, isFunction } from 'lodash';
import { ReactNode, useEffect, useMemo } from 'react';
import { Search } from 'react-feather';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import PaperSelect from 'src/components/common/PaperSelect';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import { useQueryPublicCountriesTariffs } from 'src/services/queries/useQueryPublic';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { IFrameType, usePopupRedirectStore } from 'src/zustand/usePopupRedirectStore';

interface IProps {
  id: string;
  required?: boolean;
  tooltipText?: string;
  label?: ReactNode;
  onChange?: (value: ICountryTariffs) => void;
}

const FormItemCountryTariffs = ({ id, tooltipText = '', label, required = false, onChange }: IProps) => {
  const formik = useFormikContext<ExpectedAny>();
  const { data, isFetching } = useQueryPublicCountriesTariffs();
  const { dispatch: dispatchPopup } = usePopupRedirectStore();

  const getCountryByCountryCode = (countryCode: string) => {
    if (!countryCode) {
      return {
        id: '',
        code: '',
        name: '',
        iso2: '',
        iso3: '',
        valid_from: '',
        valid_until: '',
      };
    }
    const country = find(data?.data, (o) => o.code === countryCode);

    if (!country) {
      return {
        id: '',
        code: '',
        name: '',
        iso2: '',
        iso3: '',
        valid_from: '',
        valid_until: '',
      };
    }

    return country;
  };

  const country = useMemo(() => {
    return getCountryByCountryCode(formik.values[id]);
  }, [formik.values[id], data?.data]);

  useEffect(() => {
    if (data.status_code === HttpStatusCode.TooManyRequests) {
      dispatchPopup({ visible: true, iframe: IFrameType.TariffAndRules });
    }
  }, [data]);

  return (
    <Autocomplete
      value={country}
      onChange={(_, value) => {
        if (isFunction(onChange)) onChange(value);
        formik.setFieldValue(id, value?.code);
      }}
      onBlur={formik.handleBlur}
      loading={isFetching}
      id={id}
      fullWidth
      size="small"
      autoComplete
      options={getArrayDefault(data?.data)}
      popupIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Search />} />}
      PaperComponent={PaperSelect}
      autoHighlight
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => (
        <Box gap={1} display={'flex'} component="li" {...props}>
          <FlagCountry width={24} code={option.iso2} />
          {option.name} ({option.iso2})
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{
            ...params.InputLabelProps,
          }}
          placeholder="Typing..."
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: country.code ? <FlagCountry width={24} code={country.iso2} /> : null,
          }}
          label={label ? label : <span>Your Country {tooltipText && <TooltipFormLabel title={tooltipText} />}</span>}
        />
      )}
    />
  );
};

export default FormItemCountryTariffs;
